import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { _registerToastDispatcher } from '../lib/notify';

const EXIT_MS = 200;
let uid = 0;

const ToastItem = ({ toast, onDismiss }) => {
  const [entered, setEntered] = useState(false);
  const isSuccess = toast.type === 'success';
  const Icon = isSuccess ? CheckCircle2 : AlertCircle;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const visible = entered && !toast.closing;

  return (
    <div
      className={`pointer-events-auto w-full rounded-lg border bg-white shadow-card-hover overflow-hidden transition-all duration-200 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
      } ${isSuccess ? 'border-success-200' : 'border-danger-200'}`}
    >
      <div className="flex items-start gap-3 p-3">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${isSuccess ? 'text-success-500' : 'text-danger-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink">{toast.title}</div>
          {toast.detail && <div className="text-xs text-ink-body mt-0.5">{toast.detail}</div>}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
          className="shrink-0 text-ink-body hover:text-ink rounded p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {isSuccess && toast.duration ? (
        <div className="h-0.5 bg-success-100">
          <div
            className="toast-progress-bar h-full bg-success-500"
            style={{ animationDuration: `${toast.duration}ms` }}
          />
        </div>
      ) : null}
    </div>
  );
};

// Mounted exactly once at the app root (see App.jsx). Renders nothing and
// registers no timers/listeners beyond a single dispatcher reference until a
// toast is actually pushed — zero idle cost when no toast is visible.
const ToastProvider = () => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const dismiss = useCallback((id) => {
    // Flip to "closing" so the exit transition can play, then actually drop
    // the toast from state once the transition has had time to finish.
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, closing: true } : t)));
    if (timers.current[id]) clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => removeToast(id), EXIT_MS);
  }, [removeToast]);

  const push = useCallback(({ type, title, detail, duration }) => {
    const id = ++uid;
    setToasts((prev) => [...prev, { id, type, title, detail, duration, closing: false }]);
    if (duration) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
  }, [dismiss]);

  useEffect(() => {
    _registerToastDispatcher(push);
    return () => _registerToastDispatcher(null);
  }, [push]);

  // Clear any in-flight timers if the provider itself ever unmounts.
  useEffect(() => {
    const activeTimers = timers.current;
    return () => {
      Object.values(activeTimers).forEach(clearTimeout);
    };
  }, []);

  if (toasts.length === 0) return null;

  const successToasts = toasts.filter((t) => t.type === 'success');
  const errorToasts = toasts.filter((t) => t.type === 'error');

  return createPortal(
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
      <div aria-live="polite" className="flex flex-col gap-2">
        {successToasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
      <div aria-live="assertive" className="flex flex-col gap-2">
        {errorToasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </div>,
    document.body
  );
};

export default ToastProvider;
